import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Component, Inject, OnInit } from '@angular/core';
import { BoxAccessUserService } from './box-access-user.service';
import { cpf } from 'cpf-cnpj-validator';
import { BoxAccessUsers } from '../models/BoxAccessUsers';
import { DatePipe, CommonModule } from '@angular/common';
import { BoxAccessUserPayload } from '../models/BoxAccessUserPayload';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgxMaskDirective } from 'ngx-mask';

export interface BoxAccessUserDialogData {
  id: number;
  codigoContrato: number;
}

@Component({
  selector: 'app-box-access-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSlideToggleModule, MatIconModule, MatTabsModule, MatTooltipModule, MatDialogModule, NgxMaskDirective],
  templateUrl: './box-access-user.component.html',
  styleUrls: ['./box-access-user.component.css']
})
export class BoxAccessUserComponent implements OnInit {

  constructor(
    private service: BoxAccessUserService,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) private dialogData: BoxAccessUserDialogData,
    private dialogRef: MatDialogRef<BoxAccessUserComponent>,
  ) {
     this.id = this.normalizeId(this.dialogData?.id);
     this.contrato = this.normalizeContractId(this.dialogData?.codigoContrato);
     this.mesAbreviado = {
      0: 'Jan',
      1: 'Fev',
      2: 'Mar',
      3: 'Abr',
      4: 'Mai',
      5: 'Jun',
      6: 'Jul',
      7: 'Ago',
      8: 'Set',
      9: 'Out',
      10: 'Nov',
      11: 'Dez'
    };
  }

  public insertUpdateFormBoxAccessUpdate: FormGroup = new FormGroup<any>({});
  public infoUpdateFormBoxAccessUpdate: FormGroup = new FormGroup<any>({});
  private contrato = '';
  public id = 0;
  public mesAbreviado: any;
  public isSaving = false;
  public isDeleting = false;

   public data: BoxAccessUsers = {} as BoxAccessUsers;

  static retornaDiaSemana(data: any): string{
    const diaSemana = new Date(data).getUTCDay();
    switch (diaSemana){
        case 0:
            return 'Domingo';
        case 1:
            return 'Segunda-feira';
        case 2:
            return 'Terça-feira';
        case 3:
            return 'Quarta-feira';
        case 4:
            return 'Quinta-feira';
        case 5:
            return 'Sexta-feira';
        case 6:
            return 'Sábado';
        default:
          return "";
    }
  }

  ngOnInit(): void {
    this.service.showSpinner();
    this.insertUpdateFormBoxAccessUpdate = new FormGroup({
      id: new FormControl(this.id),
      nome: new FormControl('', [Validators.required, Validators.maxLength(60)]),
      cpf: new FormControl('', [Validators.required, Validators.maxLength(15), control => {
        const value = String(control.value ?? '').trim();
        return value && !cpf.isValid(value) ? { cpf: true } : null;
      }]),
      liberado: new FormControl(true, [Validators.required]),
      observacao: new FormControl('', [Validators.maxLength(80)]),
      nomeDisplay: new FormControl('', [Validators.required, Validators.maxLength(20)]),
      codigoAcesso: new FormControl('', [Validators.required, Validators.maxLength(10), Validators.pattern(/^\d+$/)]),
    });

    this.infoUpdateFormBoxAccessUpdate = new FormGroup({
      incluido: new FormControl(''),
      atualizacao: new FormControl('')
    });

    if (!this.isValidData) {
      this.service.hideSpinner();
      this.service.toasterWarning('Contrato ou usuário de acesso inválido.');
      this.dialogRef.close();
      this.infoUpdateFormBoxAccessUpdate.disable();
      return;
    }

    if (this.isEditMode) {
      this.service.getDataBoxAccessUser(this.id).subscribe((data) => {
        this.data = data;
        if (data.ACESSO_LIBERADO !== 'S' && data.ACESSO_LIBERADO !== 'N') {
          this.service.hideSpinner();
          this.service.toasterWarning('Resposta inválida para o status do acesso.');
          this.close();
          return;
        }

        this.insertUpdateFormBoxAccessUpdate.patchValue({
          id: data.GEN_CONTRATO_CONTROLE_ACESSO,
          nome: data.NOME?.substring(0, 60) ?? '',
          cpf: data.CPF,
          liberado: data.ACESSO_LIBERADO === 'S',
          observacao: data.OBSERVACAO,
          nomeDisplay: data.NOME_DISPLAY?.substring(0, 20) ?? '',
          codigoAcesso: data.CODIGO_ACESSO,
        });
        this.infoUpdateFormBoxAccessUpdate.patchValue({
          incluido: this.dataFormatadaInformacoes(data.INCLUSAO_DATA, data.INCLUSAO_POR),
          atualizacao: this.dataFormatadaInformacoes(data.ALTERACAO_DATA, data.ALTERACAO_POR)
        });

        this.service.hideSpinner();
      }, (err) => {
        this.service.hideSpinner();
        this.service.toasterWarning(this.getErrorMessage(err, 'Não foi possível carregar o usuário de acesso.'));
        this.close();
      });

    } else {
      this.infoUpdateFormBoxAccessUpdate.patchValue({
        incluido: this.dataFormatadaInformacoes(new Date(), ''),
        atualizacao: this.dataFormatadaInformacoes(new Date(), '')
      });
      this.service.hideSpinner();
    }
    this.infoUpdateFormBoxAccessUpdate.disable();
  }

  public setBoxAccessUser(): void {
    if (!this.isValidData || this.isSaving || this.insertUpdateFormBoxAccessUpdate.invalid) {
      this.insertUpdateFormBoxAccessUpdate.markAllAsTouched();
      if (this.insertUpdateFormBoxAccessUpdate.invalid) {
        this.service.toasterWarning('Preencha os campos obrigatórios e corrija os valores informados.');
      }
      return;
    }

    const values = this.insertUpdateFormBoxAccessUpdate.getRawValue();
    const payload = {
      GEN_CONTRATO_CONTROLE_ACESSO: this.id,
      GEN_CONTRATO: Number(this.contrato),
      CPF: String(values.cpf).trim(),
      NOME: String(values.nome).trim(),
      OBSERVACAO: String(values.observacao ?? '').trim(),
      NOME_DISPLAY: String(values.nomeDisplay).trim(),
      CODIGO_ACESSO: String(values.codigoAcesso).trim(),
      ACESSO_LIBERADO:  values.liberado === true,
    } as BoxAccessUserPayload;

    this.gravarUsuarioAcessoBox(payload);
  }

  public gravarUsuarioAcessoBox(payload: BoxAccessUserPayload): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.service.showSpinner();
    this.service.setBoxAccessUser(payload).subscribe(() => {
      this.isSaving = false;
      this.service.hideSpinner();
      if (this.isEditMode) {
        this.service.toasterSuccess('Usuário alterado com sucesso!');
      } else {
        this.service.toasterSuccess('Usuário criado com sucesso!');
      }
      this.dialogRef.close({ changed: true, action: 'saved' });
    }, (err) => {
      this.isSaving = false;
      this.service.hideSpinner();
      this.service.toasterWarning(this.getErrorMessage(err, 'Não foi possível salvar as informações do usuário.'));
    });
  }

  public get isEditMode(): boolean {
    return this.id > 0;
  }

  public get isValidData(): boolean {
    return this.id >= 0 && this.contrato !== '';
  }

  loadUser(): void {
    const document = String(this.insertUpdateFormBoxAccessUpdate.value.cpf ?? '').trim();
    if (!cpf.isValid(document) && document !== '') {
      this.service.toasterWarning('CPF Inválido');
      this.insertUpdateFormBoxAccessUpdate.patchValue({
        cpf: ''
      });
      return;
    }
    if (document !== '' && cpf.isValid(document)) {
      this.service.showSpinner();
      this.service.getCadastroGeral(document)
      .subscribe((data) => {
      if ((data.NOME) && (data.NOME.length > 0)) {
          this.insertUpdateFormBoxAccessUpdate.patchValue({
            nome: data.NOME.substring(0, 60),
            cpf: data.CPF,
            nomeDisplay: data.NOME.substring(0, 20).toUpperCase(),
          });
          this.service.hideSpinner();
        } else {
          this.service.hideSpinner();
        }
      }, (err) => {
        this.service.hideSpinner();
        if (err.status !== 404){
          this.service.toasterError(this.getErrorMessage(err, 'Não foi possível consultar o CPF.'));
        }
      });
    }
  }

  public close(): void {
    if (this.isSaving || this.isDeleting) {
      return;
    }

    this.dialogRef.close();
  }

  openDialog(): void {
    if (!this.isEditMode || this.isDeleting || this.isSaving) {
      return;
    }

    if(window.confirm('Deseja realmente excluir este usuário?')){
      this.deleteUser();
    }
  }

  deleteUser(): void {
    if (!this.isEditMode || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.service.showSpinner();
    this.service.deleteBoxAccessUser(this.id).subscribe((data) => {
      this.isDeleting = false;
      this.service.toasterSuccess('Usuário excluído com sucesso!');
      this.service.hideSpinner();
      this.dialogRef.close({ changed: true, action: 'deleted' });
    }, (err) => {
      this.isDeleting = false;
      this.service.hideSpinner();
      this.service.toasterWarning(this.getErrorMessage(err, 'Erro ao excluir o usuário.'));
    });
  }

  getRandomIntInclusive(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  getAccessCode(): void {
    this.insertUpdateFormBoxAccessUpdate.patchValue({
      codigoAcesso: (('0000000000') + (this.getRandomIntInclusive(9999999, 9999999999).toString())).slice(-10)
    });
  }

  setDisplayName(): void {
    const nome = String(this.insertUpdateFormBoxAccessUpdate.value.nome ?? '').trim();
    if (nome !== '' ) {
      this.insertUpdateFormBoxAccessUpdate.patchValue({
        nomeDisplay: nome.substring(0, 20).toUpperCase()
      });
    }
  }

  private normalizeId(value: unknown): number {
    if (typeof value === 'string' && !/^\d+$/.test(value.trim())) {
      return -1;
    }

    const normalizedValue = Number(value);
    return Number.isInteger(normalizedValue) && normalizedValue >= 0 ? normalizedValue : -1;
  }

  private normalizeContractId(value: unknown): string {
    if (typeof value === 'string' && !/^\d+$/.test(value.trim())) {
      return '';
    }

    const normalizedValue = Number(value);
    return Number.isInteger(normalizedValue) && normalizedValue > 0 ? String(normalizedValue) : '';
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (typeof error?.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return fallback;
  }

  dataFormatadaInformacoes(data: Date, usuario: string = '', mesInteiro: boolean = false): string { // Formato: aaaa-mm-dd hh:mm:ss
    if (!data){
        return 'Este registro não foi modificado';
    }

    let informacaoFormatada = '';
    const dia = '';

    informacaoFormatada = this.datePipe.transform(data) ?? "(Data inválida)";
    // informacaoFormatada = this.formatarNumeroString(data.getDate()) + '/';

    // if (mesInteiro){
    //     informacaoFormatada += this.formatarNumeroString(data.getMonth() + 1);
    // } else {
    //     informacaoFormatada += this.mesAbreviado[data.getMonth()];
    // }
    // informacaoFormatada += '/';
    // informacaoFormatada += (data.getFullYear()) + ' - ';
    // informacaoFormatada += BoxAccessUserComponent.retornaDiaSemana(data) + ', às ';
    // informacaoFormatada += this.formatarNumeroString(data.getHours())
    //     + ':'
        // + this.formatarNumeroString(data.getMinutes()) + ':' + this.formatarNumeroString(data.getSeconds());
    if ( usuario && usuario.trim() !== '') {
      informacaoFormatada += ' por ' + usuario;
    } else {
      informacaoFormatada += ' por *Cliente';
    }
    return informacaoFormatada;
  }

  formatarNumeroString = (numero: number): string => {
    if (numero < 9){
        return '0' + String(numero);
    }
    return numero.toString();
  }
}
